import { serialize } from "object-to-formdata";
import request from "../../utils/request";
import {
  CompanyView, Company, CompanyInput, CompanyUpdateInput, ComplianceCountry, Result, SubscriptionPlan, WorkplaceModel,
  Branch, Department, CompanyRulesView, CompanyRulesInput,
  BranchInput,
  DepartmentInput,
  BatchMigrationSetupResult
} from "../../models/general";
import { findApiError } from "../../utils/requestHandling";
import { WeekScheduleView, WeekScheduleInput,UserActivityView } from "../../models/attendance";


// export async function getLeavePerUser(userId: string) {
//    const response = await request.get<Result<LeavesModel[]>>(`Leave/GetLeavePerUser/${userId}`);
//    return response.data;
// }

// export async function getBalancePerUser(userId: string) {
//    const response = await request
//        .get<Result<LeaveBalanceModel[]>>(`Leave/GetBalancePerUser/${userId}`);
//    return response.data;
// }

export async function getCompanies(organizationId?: number) {
  const response = await request
    .get<Result<CompanyView[]>>(`Company/GetCompanies/${organizationId ?? ""}`);
  return response.data;
}

export async function getCompany(id: string | number) {
  const response = await request.get<Result<CompanyView>>(`Company/${id}`);
  return response.data;
}

export async function addCompany(input: CompanyInput) {
  const response = await request
    .post<Result<Company>>("Company/AddCompany", serialize(input));
  return response.data;
}

// CO-227 — extendTrial() and activateTrialCompany() removed. Trial is retired and the two
// endpoints they called (Company/ExtendTrial, Company/ActivateTrialCompany) no longer exist.

export async function getPlans() {
  const response = await request.get<Result<SubscriptionPlan[]>>("Company/Plans");
  return response.data;
}

/**
 * CO-879 - `Error.code` of the HTTP 409 that `PUT Company/{id}` answers when the request carries a
 * CountryId DIFFERENT from the stored one that would move the company's compliance country, without
 * `ConfirmComplianceCountryChange=true`. Mirror of
 * `CompanyService.ComplianceCountryChangeRequiresConfirmationCode` on the server: a contract, do not
 * rename one without the other.
 */
export const COMPLIANCE_COUNTRY_CHANGE_REQUIRES_CONFIRMATION = "complianceCountryChangeRequiresConfirmation";

const COMPLIANCE_COUNTRIES: readonly ComplianceCountry[] = ["None", "UAE", "KSA"];

/** `value` as a compliance country, or undefined when it is not one of the three. Never throws. */
export function toComplianceCountry(value: unknown): ComplianceCountry | undefined {
  return COMPLIANCE_COUNTRIES.find((c) => c === value);
}

/**
 * The compliance country a country's ISO code maps to. Mirror of `CompanyService.MapComplianceCountry`
 * on the server (ae -> UAE, sa -> KSA, anything else -> None). Use it for the country a user PICKS
 * (the "to" side of a change), to warn before a save; the server decides, and says so with the 409
 * above when the two disagree. Never use it for what a company runs on TODAY: that is the stored
 * `CompanyView.complianceCountry`, which can differ from the mapping of the stored country.
 */
export function complianceCountryForIso(iso?: string | null): ComplianceCountry {
  switch ((iso ?? "").trim().toLowerCase()) {
    case "ae": return "UAE";
    case "sa": return "KSA";
    default: return "None";
  }
}

/** The before / after of a compliance-country change. A side the server did not name is undefined. */
export interface ComplianceCountryChange {
  from?: ComplianceCountry;
  to?: ComplianceCountry;
  /**
   * The country the SERVER holds for the company (`details.previousCountryId`): null when it has
   * none, undefined when the server did not say. It differs from what a screen holds as stored when
   * the country was changed elsewhere after that screen loaded.
   */
  previousCountryId?: number | null;
  /** The country the refused request asked for (`details.newCountryId`). */
  newCountryId?: number;
}

const toCountryId = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

/**
 * Reads the "confirmation required" refusal off a failed `updateCompany`. Returns null for any other
 * failure. The request helper REJECTS every non-2xx, so call this in the `catch`; it also accepts a
 * resolved `Result` so a caller can pass whatever it was handed. Never throws.
 */
export function complianceCountryChangeRefusal(failure: unknown): ComplianceCountryChange | null {
  const refusal = findApiError(failure, COMPLIANCE_COUNTRY_CHANGE_REQUIRES_CONFIRMATION);
  if (!refusal) return null;
  const details = (refusal.details && typeof refusal.details === "object" ? refusal.details : {}) as {
    previousComplianceCountry?: unknown;
    newComplianceCountry?: unknown;
    previousCountryId?: unknown;
    newCountryId?: unknown;
  };
  return {
    from: toComplianceCountry(details.previousComplianceCountry),
    to: toComplianceCountry(details.newComplianceCountry),
    previousCountryId: details.previousCountryId === null ? null : toCountryId(details.previousCountryId),
    newCountryId: toCountryId(details.newCountryId),
  };
}

const toCountryIds = (values: unknown): number[] =>
  Array.isArray(values) ? values.map(toCountryId).filter((id): id is number => id !== undefined) : [];

/**
 * ST-DIALOG (statutory leave templates per company, spec section 5.2) - the countries a country change
 * would add statutory leave copies for: each has a live platform catalogue row the CALLER's company holds
 * no copy of (an archived copy counts as held, as on the server's ensure). Hr and Admin. Feeds one line of
 * the country-change notice, so it never throws: any failure is an empty list and the line stays hidden.
 * The save is unaffected either way, since the server adds the copies itself. Under `/api/Leave`, so a
 * Free company gets the tier lock's 403 and its upgrade toast: do not call it for a Free company.
 */
export async function getStatutoryCountriesToCopy(): Promise<number[]> {
  try {
    const response = await request.get<Result<number[]>>("Leave/StatutoryTemplate/CountriesToCopy");
    return toCountryIds(response.data?.payload);
  } catch {
    return [];
  }
}

/**
 * ST-DIALOG - the Operator's twin of `getStatutoryCountriesToCopy`: the countries the live platform
 * catalogue has rows for. It ignores what the company being edited already holds, so the line can show
 * for a country whose copies that company has already. Operator only. Never throws: failure is [].
 */
export async function getStatutoryCatalogueCountryIds(): Promise<number[]> {
  try {
    const response = await request.get<Result<{ countryId?: unknown }[]>>("Operator/StatutoryTemplate");
    const rows = Array.isArray(response.data?.payload) ? response.data.payload : [];
    return Array.from(new Set(toCountryIds(rows.map((row) => row?.countryId))));
  } catch {
    return [];
  }
}

/**
 * CO-879 - PARTIAL update. A field left `undefined` / `null` is not put in the form, and the server
 * keeps its stored value; a text field sent as "" is cleared. So:
 *  - to CLEAR a text field pass "" (never undefined: `PhoneInput` yields undefined when emptied);
 *  - to leave a field alone, leave it out.
 * Country and compliance country (statutory payroll rules):
 *  - leave `countryId` out (or send the stored id) and nothing about them changes, never a 409;
 *  - a `countryId` DIFFERENT from the stored one that would move the compliance country is refused
 *    with a 409 unless `confirmComplianceCountryChange` is true; read that refusal with
 *    `complianceCountryChangeRefusal`;
 *  - `confirmComplianceCountryChange: true` is never refused and sets the compliance country to the
 *    one the effective country (the one sent, else the stored one) maps to.
 * So a screen sends `countryId` only when the user changed it or ticked a confirmation.
 * The 200 payload is the Company ENTITY; it carries `countryId` and `complianceCountry` as saved.
 */
export async function updateCompany(id: number, input: CompanyUpdateInput) {
  // Build FormData manually with PascalCase keys to match the server's CompanyUpdateInput.
  const fd = new FormData();
  const append = (key: string, val: unknown) => {
    if (val === undefined || val === null) return;
    if (val instanceof File) { fd.append(key, val); return; }
    fd.append(key, String(val));
  };

  append('Name', input.name);
  append('CountryId', input.countryId);
  append('TimeZone', input.timeZone);
  append('PlanId', input.planId);
  append('OrganizationId', input.organizationId);
  append('Address', input.address);
  append('PhoneNumber', input.phoneNumber);
  append('Email', input.email);
  append('WebsiteLink', input.websiteLink);
  append('PrimaryColor', input.primaryColor);
  append('Locales', input.locales);
  append('AdminFirstName', input.adminFirstName);
  append('AdminLastName', input.adminLastName);
  append('AdminEmail', input.adminEmail);
  append('EmployerNarrative', input.employerNarrative);
  append('MOLNumber', input.molNumber);
  append('CommercialRegistrationNumber', input.commercialRegistrationNumber);
  append('MudadEstablishmentNumber', input.mudadEstablishmentNumber);
  append('QiwaEntityId', input.qiwaEntityId);
  // Sent only as the literal "true": the server binds a bool, and absent already means false.
  if (input.confirmComplianceCountryChange === true) fd.append('ConfirmComplianceCountryChange', 'true');
  if (input.image) fd.append('Image', input.image as unknown as File);
  if (input.rolesMaxUsers) {
    for (const [role, max] of Object.entries(input.rolesMaxUsers)) {
      fd.append(`RolesMaxUsers[${role}]`, String(max));
    }
  }

  const response = await request.put<Result<Company>>(`Company/${id}`, fd);
  return response.data;
}

export async function deleteCompany(id: number) {
  const response = await request.delete<Result<Record<string, never>>>(`Company/${id}`);
  return response.data;
}

export async function getUsersLocation(companyId: number, _onlyAvailable: boolean) {
  const response = await request.get<Result<UserActivityView[]>>(`Company/${companyId}/Location`);
  return response.data;
}

export async function getWorkplaces(companyId: number, userId: string, userSpecificOnly?: boolean) {
  const params = new URLSearchParams();
  if (userId) params.append("userId", userId);
  if (userSpecificOnly) params.append("userSpecificOnly", "true");
  const qs = params.toString();
  const response = await request.get<Result<WorkplaceModel[]>>(
    `Company/${companyId}/Workplaces${qs ? `?${qs}` : ""}`,
  );
  return response.data;
}

export async function addWorkplace(companyId: number, userId: string, workplace: WorkplaceModel) {
  const response = await request.post<Result<WorkplaceModel>>(`Company/AddWorkplace/${companyId}/${userId}`, workplace);
  return response.data;
}

export async function removeWorkplace(workplaceId: number) {
  const response = await request.delete<Result<boolean>>(`Company/Workplaces/${workplaceId}`);
  return response.data;
}

export async function updateWorkplace(workplaceId: number, workplace: WorkplaceModel) {
  const response = await request.put<Result<WorkplaceModel>>(`Company/EditWorkplace/${workplaceId}`, workplace);
  return response.data;
}

export async function getBranches(companyId: number) {
  const response = await request.get<Result<Branch[]>>(`Company/Branches/${companyId}`);
  return response.data;
}

export async function getDepartments(companyId: number) {
  const response = await request.get<Result<Department[]>>(`Company/Departments/${companyId}`);
  return response.data;
}
export async function getCountryDepartments(countryId: number[], companyId: number) {
  const response = await request.post<Result<Department[]>>(`Company/GetCountryDepartments/${companyId}`, countryId);
  return response.data;
}
export async function getCountryBranches(countryId: number[], companyId: number) {
  const response = await request.post<Result<Branch[]>>(`Company/GetCountryBranches/${companyId}`, countryId);
  return response.data;
}
export async function getBranchDepartments(branchId: number[], companyId: number) {
  const response = await request.post<Result<Department[]>>(`Company/GetBranchDepartments/${companyId}`, branchId);
  return response.data;
}
export async function addUpdateBranches(branches: BranchInput[]) {
  const response = await request.post<Result<Branch[]>>(`Company/AddUpdateBranches`, branches);
  return response.data;
}

export async function addUpdateDepartments(departments: DepartmentInput[]) {
  const response = await request.post<Result<Department[]>>(`Company/AddUpdateDepartments`, departments);
  return response.data;
}

export async function deleteBranches(branchIds: number[]) {
  const response = await request.delete<Result<string>>(`Company/Branches`, { data: branchIds });
  return response.data;
}

export async function deleteDepartments(departmentIds: number[]) {
  const response = await request.delete<Result<string>>(`Company/Departments`, { data: departmentIds });
  return response.data;
}

export async function batchMigrationSetup(companyId: number, input: { branches: BranchInput[]; departments: DepartmentInput[] }) {
  const response = await request.post<Result<BatchMigrationSetupResult>>(`Company/BatchMigrationSetup/${companyId}`, input);
  return response.data;
}

export async function getCompanyRules(companyId: number) {
  const response = await request.get<Result<CompanyRulesView>>(`Company/GetCompanyRules/${companyId}`);
  return response.data;
}

/**
 * CO-375 / E13 (review-2 finding 5): the company notification master switch has ONE writer, Settings >
 * Notification management (PUT api/NotificationModules/master). Almost every company-rules editor (Wialon,
 * engagement jobs, asset replenishment, attendance pay, hourly pay, payroll basis, work schedule, Operator
 * company rules) spreads the loaded CompanyRulesView back into its POST, which carried a load-time snapshot of
 * `disableAllNotifications` and silently rewrote a switch HR had just changed. Removed here for EVERY caller
 * (the server also ignores it now); removing the key, not nulling it, is what the server's bool? expects.
 */
function withoutMasterSwitch(input: CompanyRulesInput): CompanyRulesInput {
  const out = { ...input };
  delete out.disableAllNotifications;
  return out;
}

export async function updateCompanyRules(companyId: number, input: CompanyRulesInput) {
  const response = await request.post<Result<CompanyRulesView>>(`Company/UpdateCompanyRules/${companyId}`, withoutMasterSwitch(input));
  return response.data;
}

export async function operatorUpdateCompanyRules(companyId: number, input: CompanyRulesInput) {
  const response = await request.post<Result<CompanyRulesView>>(`Company/OperatorUpdateCompanyRules/${companyId}`, withoutMasterSwitch(input));
  return response.data;
}

export async function getWeekSchedule(companyId: string | number) {
  const response = await request.get<Result<WeekScheduleView>>(`Company/GetWeekSchedule/${companyId}`);
  return response.data;
}
export async function setWeekSchedule(details: WeekScheduleInput) {
  const response = await request.post<Result<WeekScheduleView>>(`Company/SetWeekSchedule`, details);
  return response.data;
}
